import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface TypewriterProps {
  staticText?: string;
  phrases: string[];
  className?: string;
  speed?: number;
  deleteSpeed?: number;
  delayBetweenPhrases?: number;
}

export function Typewriter({
  staticText = '',
  phrases,
  className,
  speed = 50,
  deleteSpeed = 15,
  delayBetweenPhrases = 2000,
}: TypewriterProps) {
  const [displayText, setDisplayText] = useState(phrases[0] || ''); // Start with first phrase for SEO
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const currentPhrase = phrases[phraseIndex];

    const type = () => {
      if (isDeleting) {
        if (displayText === '') {
          setIsDeleting(false);
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
          timeout = setTimeout(type, delayBetweenPhrases);
        } else {
          setDisplayText((prev) => prev.slice(0, -1));
          timeout = setTimeout(type, deleteSpeed);
        }
      } else {
        if (displayText === currentPhrase) {
          timeout = setTimeout(() => {
            setIsDeleting(true);
            type();
          }, delayBetweenPhrases);
        } else {
          setDisplayText((prev) => currentPhrase.slice(0, prev.length + 1));
          timeout = setTimeout(type, speed);
        }
      }
    };

    // Start animation after first phrase is already visible (delay for UX)
    timeout = setTimeout(type, displayText === phrases[0] ? delayBetweenPhrases : speed);

    const cursorInterval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 530);

    return () => {
      clearTimeout(timeout);
      clearInterval(cursorInterval);
    };
  }, [displayText, phraseIndex, isDeleting, phrases, speed, deleteSpeed, delayBetweenPhrases]);

  return (
    <span className={cn('inline', className)}>
      <span className="whitespace-pre-wrap">{staticText}</span>
      <span className="relative inline-block min-h-[1.2em]">
        <span className="text-white">{displayText}</span>
        <span
          className={cn(
            'absolute ml-1',
            'border-r-4 border-primary',
            'h-[1.1em] inline-block',
            cursorVisible ? 'opacity-100' : 'opacity-0',
            'transition-opacity duration-100'
          )}
        />
      </span>
    </span>
  );
}
